import { loadPortraitData, startPortrait, type PortraitData } from "../portrait/portrait.js";
import { loadXrayDots, type XraySource } from "../portrait/xray.js";
import type { XrayDots } from "@bfly/engine";
import type { PortraitHooks } from "../waves/waves.js";

// The portrait: `#portrait`, with its static print, its canvases, and the butterfly's button as
// children. It starts on connect. The first connection starts downloading the data, and every
// connection sets the portrait up again with that data (portrait.ts), on a signal that
// disconnecting aborts. A new wave fires a `jrsm-wave` event on the element; the per-frame data
// goes through `hooks`, which the page sets.
export class JrsmPortrait extends HTMLElement {
  /** The page's frame hooks. The portrait looks them up fresh on every call, so the page can
      set them after the upgrade (the data arrives later). */
  hooks: PortraitHooks = {};
  private readonly relay: Required<PortraitHooks> = {
    onOutline: (outline) => this.hooks.onOutline?.(outline),
    onWaves: (rings) => this.hooks.onWaves?.(rings),
    wavesHeld: () => this.hooks.wavesHeld?.() ?? false,
  };
  private loading: Promise<PortraitData> | null = null;
  private connection: AbortController | null = null;
  private readonly shown = Promise.withResolvers<void>();
  private readonly xrayAllowed = Promise.withResolvers<void>();
  private xrayLoading: Promise<XrayDots> | null = null;
  // One download for the element's life: a new connection draws the same dots.
  private readonly xray: XraySource = {
    allowed: this.xrayAllowed.promise,
    load: () => (this.xrayLoading ??= loadXrayDots()),
  };
  /** Settles once the first connection's portrait shows: its first frame, or the print. */
  readonly ready: Promise<void> = this.shown.promise;

  /** Lets the X-ray's dots load. The page calls this once it has shown and finished loading,
      since the X-ray isn't part of the first view. The print and reduced motion never load
      them, because no wave can show them there. */
  loadXray(): void {
    this.toggleAttribute("data-xray", true);
    this.xrayAllowed.resolve();
  }

  connectedCallback(): void {
    // The element upgrades as soon as its module loads, before the rest of the page's setup,
    // which would hold up the request (the text effects wrap and measure the whole page).
    this.loading ??= loadPortraitData();
    this.connection?.abort();
    this.connection = new AbortController();
    // A new connection gives the live portrait another try.
    this.toggleAttribute("data-static", false);
    void startPortrait(this, this.relay, this.loading, this.connection.signal, this.xray).then(
      this.shown.resolve,
    );
  }

  disconnectedCallback(): void {
    this.connection?.abort();
    this.connection = null;
    // A lost context can't draw again, so a later connection gets fresh canvases.
    for (const canvas of this.querySelectorAll("canvas.face, canvas.lens, canvas.bfly-cv"))
      canvas.replaceWith(canvas.cloneNode(false));
  }
}

customElements.define("jrsm-portrait", JrsmPortrait);
